import axios from 'axios';
import React, { memo, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useChatChanges } from '@/app/providers/chat-changes-provider/chat-changes-provider';
import { useTheme } from '@/app/providers/theme-provider';
import { IMessage } from '@/shared/api/types/chat-message';
import { env } from '@/shared/lib';
import { MarkdownDndItem } from '@/widgets/conversation-reports-graph/types';

import { PLAN_STYLES } from '../../consts';
import { CHAT_BILLING_BLOCKED_TEXT } from '../../model/map-billing-status-to-chat-event';
import Box from '../../ui/box';
import Button from '../../ui/button';
import CalendarMessage from '../calendar-preview-message/calendar-preview-message';
import JiraActionResultMessage from '../jira-action-result-message/jira-action-result-message';
import JiraPreviewMessage from '../jira-preview-message/jira-preview-message';
import { MessageFeedback } from '../message-feedback/message-feedback';
import { MessageWithDelay } from '../message-with-delay/message-with-delay';
import StatusMessage from '../status-message/status-message';

const showChatIntegrations = env.VITE_SHOW_CHAT_INTEGRATIONS === 'true';

interface Props {
  protocol_id: string;
  messages: IMessage[];
  thinking: boolean;
  onReply: (msg: IMessage) => void;
  selections: Record<string, Record<string, boolean>>;
  setSelections: React.Dispatch<React.SetStateAction<Record<string, Record<string, boolean>>>>;
  ws: React.RefObject<WebSocket | null>;
  setMessages: React.Dispatch<React.SetStateAction<IMessage[]>>;
  isProcessing: boolean;
}

const MessagesList = ({
  protocol_id,
  messages,
  thinking,
  onReply,
  selections,
  setSelections,
  ws,
  setMessages,
  isProcessing,
}: Props) => {
  const toggle = (msgId: string, itemId: string) => {
    setSelections((prev) => ({
      ...prev,
      [msgId]: {
        ...prev[msgId],
        [itemId]: !prev[msgId]?.[itemId],
      },
    }));
  };

  const handleApply = (msg: IMessage) => {
    const msgSelections = selections[msg.id as string] ?? {};

    const selected = Object.entries(msgSelections)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, checked]) => checked)
      .map(([id]) => id);

    const applyMsg = {
      text: '', // `Применить план (выбрано: ${selected.length})`,
      protocol_id,
      reply_to: msg.id,
      reply_to_type: msg.type,
      selected,
    };

    ws.current?.send(JSON.stringify(applyMsg));

    setMessages((prev) => [
      ...prev,
      {
        id: uuidv4(),
        sender: 'user',
        type: 'user',
        payload: {
          text: `✓ Применить план (выбрано: ${selected.length})`,
          reply_to: msg.id,
        },
        createdAt: Date.now(),
      },
    ]);
  };

  const getMessageById = (id: string | null) => messages.find((m) => m.id === id);

  const endRef = useRef<HTMLDivElement>(null);

  const { theme } = useTheme();

  const {
    protocolUpdates,
    setProtocolUpdates,
    clearProtocolUpdates,
    isAnimating,
    applyChanges,
    declineChanges,
    hasResolvedBatch,
    setHasResolvedBatch,
  } = useChatChanges();

  const handleApplyChanges = async () => {
    // console.log(protocolUpdates);
    const list_fields = ['decisions', 'deadlines', 'responsible', 'participants', 'chunks_ids'];

    const VALID_FIELDS: Record<string, string[]> = {
      TopicItem: ['name', 'description', 'decisions', 'deadlines', 'responsible'],
      ProtocolItem: ['content'],
    };

    const allChanges = protocolUpdates.flatMap((update) => update.payload.changes);
    // сonst mockProtocol = '69c85d30-e0da-4e8b-9560-d332d476501f';

    const filteredChanges = allChanges.filter((change) =>
      VALID_FIELDS[change.type]?.includes(change.field),
    );

    try {
      await Promise.all(
        filteredChanges.map((change) =>
          axios.patch(
            `https://protocolist-server.flowai.ru/conversations/${protocol_id}/nodes/${change.id}`,
            {
              fields: {
                [change.field]: list_fields.includes(change.field)
                  ? Array.isArray(change.after)
                    ? change.after
                    : [change.after]
                  : change.after,
              },
            },
          ),
        ),
      );
    } catch (error) {
      console.error('Error applying changes:', error);
    }

    applyChanges();

    clearProtocolUpdates();
  };

  const handleDeclineChanges = () => {
    declineChanges();
    clearProtocolUpdates();
  };

  type MappedMessage = IMessage & {
    __batchId?: string;
  };

  type BatchMapResult = {
    messages: MappedMessage[];
    lastUnresolvedUpdates: IMessage[];
  };

  const mapProtocolBatches = (messages: IMessage[]): BatchMapResult => {
    const result: MappedMessage[] = [];
    const batches: IMessage[][] = [];

    let currentBatch: IMessage[] = [];

    // 1. режем messages → batches по final:true
    for (const msg of messages) {
      currentBatch.push(msg);

      if (msg.final === true) {
        batches.push(currentBatch);
        currentBatch = [];
      }
    }

    // хвост без final — считаем батчем
    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }

    let lastUnresolvedUpdates: IMessage[] = [];

    // 2. обрабатываем каждый батч
    batches.forEach((batch, batchIndex) => {
      const batchId = `batch-${batchIndex}`;
      // @ts-expect-error -- fuck off
      const lastMsg = batch.at(-1);

      const protocolUpdates = batch.filter((m) => m.type === 'protocol_update');

      const unresolvedUpdates = protocolUpdates.filter((m) => !m.payload?.resolved);

      // добавляем все сообщения батча
      batch.forEach((m) => {
        result.push({
          ...m,
          __batchId: batchId,
        });
      });

      if (!lastMsg) return;

      const shouldAddMockSystem =
        protocolUpdates.length > 0 && lastMsg.type !== 'text' && lastMsg.type !== 'question';

      if (shouldAddMockSystem) {
        result.push({
          id: lastMsg.id, //`mock-${batchId}`,
          sender: 'assistant',
          type: 'mock-system',
          payload: {
            text: `✓ Готово! Выполнено ${protocolUpdates.length} изменений в протоколе.`,
          },
          feedback: lastMsg.feedback,
          createdAt: Date.now(),
          __batchId: batchId,
        });
      }

      // если это ПОСЛЕДНИЙ батч — запоминаем unresolved
      if (batchIndex === batches.length - 1 && unresolvedUpdates.length > 0) {
        lastUnresolvedUpdates = unresolvedUpdates;
      }
    });

    return {
      messages: result,
      lastUnresolvedUpdates,
    };
  };

  const { messages: mappedMessages, lastUnresolvedUpdates } = React.useMemo(
    () => mapProtocolBatches(messages),
    [messages],
  );

  useEffect(() => {
    if (lastUnresolvedUpdates.length === 0) return;

    setProtocolUpdates(lastUnresolvedUpdates);
    setHasResolvedBatch(false);
  }, [lastUnresolvedUpdates, setProtocolUpdates, setHasResolvedBatch]);

  // const collectLastProtocolBatch = (messages: IMessage[], includeResolved = false) => {
  //   const updates: IMessage[] = [];

  //   // 1. ищем последний final, который ещё не обработан (или includeResolved)
  //   const finalIndex = [...messages]
  //     .reverse()
  //     .findIndex((m) => m.final === true && (includeResolved || !m.payload?.resolved));

  //   if (finalIndex === -1) return [];

  //   const realFinalIndex = messages.length - 1 - finalIndex;

  //   // 2. начинаем С САМОГО final-сообщения
  //   for (let i = realFinalIndex; i >= 0; i--) {
  //     const m = messages[i];

  //     // стоп: предыдущий батч или пользователь
  //     if (i !== realFinalIndex && m.final === true) break;
  //     if (m.sender === 'user') break;

  //     if (m.type === 'protocol_update') {
  //       updates.unshift(m);
  //       continue;
  //     }

  //     // UI-шум игнорируем
  //     if (
  //       m.type === 'text' ||
  //       m.type === 'question' ||
  //       m.type === 'progress' ||
  //       m.type === 'mock-system'
  //     ) {
  //       continue;
  //     }

  //     break;
  //   }

  //   return updates;
  // };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  // useEffect(() => {
  //   //@ts-expect-error - compiler error
  //   const last = messages.at(-1);
  //   if (!last?.final) return;

  //   const collected = collectLastProtocolBatch(messages);
  //   if (collected.length === 0) return;

  //   setProtocolUpdates(collected);
  //   setHasResolvedBatch(false);

  //   if (last.type === 'text' || last.type === 'question') {
  //     return;
  //   }

  //   setMessages((prev) => {
  //     //@ts-expect-error - compiler error
  //     if (prev.at(-1)?.type === 'mock-system') return prev;

  //     return [
  //       ...prev,
  //       {
  //         id: last.id ?? uuidv4(),
  //         sender: 'assistant',
  //         type: 'mock-system',
  //         payload: {
  //           text: `✓ Готово! Выполнено ${collected.length} изменений в протоколе.`,
  //         },
  //         createdAt: Date.now(),
  //         feedback: last.feedback,
  //       },
  //     ];
  //   });
  // }, [messages]);

  // useEffect(() => {
  //   //@ts-expect-error - compiler error
  //   const last = messages.at(-1);
  //   const collected = collectLastProtocolBatch(messages, true);
  //   if (last?.final && last.type === 'protocol_update' && collected.length > 0) {
  //     setMessages((prev) => {
  //       //@ts-expect-error - compiler error
  //       if (prev.at(-1)?.type === 'mock-system') return prev;

  //       return [
  //         ...prev,
  //         {
  //           id: uuidv4(),
  //           sender: 'assistant',
  //           type: 'mock-system',
  //           payload: {
  //             text: `✓ Готово! Выполнено ${collected.length} изменений в протоколе.`,
  //           },
  //           createdAt: Date.now(),
  //         },
  //       ];
  //     });
  //   }
  // }, [messages]);

  return (
    <Box>
      {mappedMessages.map((msg) => {
        if (msg.sender === 'user') {
          const replied = msg.payload?.reply_to ? getMessageById(msg.payload.reply_to) : null;

          return (
            <div key={msg.id} className="flex flex-col mb-6">
              {replied && (
                <div className="mt-1 px-2 py-1 text-sm text-gray-600 bg-[#F3F4F6] rounded-lg max-w-[320px] truncate">
                  Ответ на: {replied.payload?.text ?? 'Сообщение'}
                </div>
              )}
              <div className="bg-[#c7e3ff] px-3 py-2 rounded-lg self-start max-w-[340px] text-black">
                {msg.payload?.text}

                {Array.isArray(msg.payload?.entities) && msg.payload.entities.length > 0 && (
                  <div className="mt-2 flex flex-col gap-1 text-xs text-gray-700">
                    <span className="font-medium text-gray-500">Фрагменты:</span>

                    <ul className="list-disc pl-4 space-y-0.5">
                      {msg.payload.entities.map((e: MarkdownDndItem, i: number) => (
                        <li key={`${e.entityId}:${e.field}:${i}`} className="truncate">
                          {e.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        }

        if (
          showChatIntegrations
            ? msg.type === 'mock-system' ||
              msg.type === 'text' ||
              msg.type === 'question' ||
              msg.type === 'calendar_response' ||
              msg.type === 'jira_response'
            : msg.type === 'mock-system' || msg.type === 'text' || msg.type === 'question'
        ) {
          // const notToPost = msg.type === 'calendar_response' || msg.type === 'jira_response';
          // const protocolUpdates = notToPost ? [] : collectLastProtocolBatch(messages);
          const hasProtocolUpdates = protocolUpdates.length > 0;
          const isLast = messages[messages.length - 1]?.id === msg.id;
          // console.log('isAnimating:', isAnimating);
          // console.log('hasResolvedBatch:', hasResolvedBatch);
          // console.log('!isLast:', !isLast);
          const isDisabled = isAnimating || hasResolvedBatch; // || !isLast;
          // console.log('isDisabled:', isDisabled);
          const applyStyles = PLAN_STYLES['plan'];
          const declineStyles = PLAN_STYLES['decline'];
          const isLegacyBillingBlockedNotice =
            msg.sender === 'assistant' &&
            msg.type === 'text' &&
            msg.payload?.text === CHAT_BILLING_BLOCKED_TEXT;
          const isBillingBlockedSystemNotice =
            msg.payload?.system_notice === 'billing_blocked' || isLegacyBillingBlockedNotice;

          if (isBillingBlockedSystemNotice) {
            return (
              <div key={msg.id} className="flex justify-end mb-6">
                <div className="px-3 py-2 rounded-lg max-w-[340px] flex flex-col text-black bg-[#F7F7F7]">
                  <MessageWithDelay
                    className="text-black"
                    withMarkdown
                    text={msg.payload.text}
                    delay={10}
                  />
                </div>
              </div>
            );
          }

          return (
            <div key={msg.id} className="flex justify-end mb-6">
              <div
                className={`px-3 py-2 rounded-lg max-w-[340px] flex flex-col text-black 
                ${
                  msg.type === 'question'
                    ? 'bg-yellow-100 border border-yellow-300'
                    : 'bg-[#F7F7F7]'
                }`}
              >
                <MessageWithDelay
                  className="text-black"
                  withMarkdown
                  text={msg.payload.text}
                  delay={10}
                />
                {/* {protocolUpdates.map((pu) =>
                  pu.payload.changes.map((change: any, index: number) => (
                    <div key={`${pu.id}-${index}`}>
                      <p className={`${PLAN_STYLES['decline'].container} p-2 rounded-md mt-2`}>
                        <strong className={`${PLAN_STYLES['decline'].title}`}>Изменяем с:</strong>{' '}
                        <MessageWithDelay withMarkdown text={change.before} delay={10} />
                      </p>

                      <p className={`${PLAN_STYLES['plan'].container} p-2 rounded-md mt-2`}>
                        <strong className={`${PLAN_STYLES['plan'].title}`}>На:</strong>{' '}
                        <MessageWithDelay withMarkdown text={change.after} delay={10} />
                      </p>
                    </div>
                  )),
                )} */}
                <MessageFeedback messageId={msg.id as string} feedback={msg.feedback} />
                <div className="flex justify-between gap-4 max-h-[32px] items-center mt-[12px]">
                  <Button
                    className="text-sm !text-gray-600 cursor-pointer !bg-transparent"
                    onClick={() => onReply(msg)}
                  >
                    ↩ ответить
                  </Button>
                  {hasProtocolUpdates && isLast && (
                    <div className="flex gap-2 items-center">
                      <Button
                        className={`${applyStyles.button} text-white px-3 py-1 rounded text-sm`}
                        onClick={handleApplyChanges}
                        disabled={isDisabled}
                      >
                        Применить
                      </Button>
                      <Button
                        className={`${declineStyles.button} text-white px-3 py-1 rounded text-sm`}
                        onClick={handleDeclineChanges}
                        disabled={isDisabled}
                      >
                        Отменить
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        if (msg.type === 'plan' || msg.type === 'plan_update') {
          const styles = PLAN_STYLES[msg.type];
          const items = msg.payload?.items || [];
          const isLast = messages[messages.length - 1]?.id === msg.id;

          return (
            <div key={msg.id} className="flex justify-end mb-6">
              <div className={`${styles.container} px-4 py-3 rounded-lg max-w-[360px]`}>
                <div className={`${styles.title} font-medium mb-2`}>
                  <MessageWithDelay
                    withMarkdown
                    text={msg.payload.text}
                    delay={10}
                    className={`${styles.title} font-medium mb-2`}
                  />
                </div>

                {items.map((item: any) => (
                  <label key={`${item.field}:${item.id}`} className="flex gap-2 items-center py-1">
                    <input
                      type="checkbox"
                      checked={selections[msg.id as string]?.[item.id] ?? item.checked}
                      onChange={() => toggle(msg.id as string, item.id)}
                      disabled={!isLast}
                    />
                    <MessageWithDelay
                      className="text-black"
                      withMarkdown
                      text={item.text}
                      delay={10}
                    />
                  </label>
                ))}

                <MessageFeedback messageId={msg.id as string} feedback={msg.feedback} />

                <div className="flex mt-3 items-center justify-between gap-4">
                  <Button
                    className="text-sm !text-gray-600 cursor-pointer !bg-transparent"
                    onClick={() => onReply(msg)}
                  >
                    ↩ ответить
                  </Button>
                  <Button
                    className={`${styles.button} text-white px-3 py-1 rounded`}
                    onClick={() => handleApply(msg)}
                    disabled={!isLast}
                  >
                    Применить
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        // if (msg.type === 'mock-system') {
        //   const protocolUpdates = collectLastProtocolBatch(messages);
        //   const hasProtocolUpdates = protocolUpdates.length > 0;
        //   const isLast = messages[messages.length - 1]?.id === msg.id;
        //   // console.log('isAnimating:', isAnimating);
        //   // console.log('hasResolvedBatch:', hasResolvedBatch);
        //   // console.log('!isLast:', !isLast);
        //   const isDisabled = isAnimating || hasResolvedBatch || !isLast;
        //   // console.log('isDisabled:', isDisabled);
        //   const applyStyles = PLAN_STYLES['plan'];
        //   const declineStyles = PLAN_STYLES['decline'];

        //   return (
        //     <div key={msg.id} className="flex justify-end mb-6">
        //       <div
        //         className="px-3 py-2 rounded-lg max-w-[340px] flex flex-col
        //         "
        //       >
        //         <MessageWithDelay
        //           className="text-black"
        //           withMarkdown
        //           text={msg.payload.text}
        //           delay={10}
        //         />
        //         <div className="flex justify-between gap-4 max-h-[32px] items-center mt-[12px]">
        //           <Button
        //             className="text-sm !text-gray-600 cursor-pointer !bg-transparent"
        //             onClick={() => onReply(msg)}
        //           >
        //             ↩ ответить
        //           </Button>
        //           {hasProtocolUpdates && (
        //             <div className="flex gap-2 items-center">
        //               <Button
        //                 className={`${applyStyles.button} text-white px-3 py-1 rounded text-sm`}
        //                 onClick={handleApplyChanges}
        //                 disabled={isDisabled}
        //               >
        //                 Применить
        //               </Button>
        //               <Button
        //                 className={`${declineStyles.button} text-white px-3 py-1 rounded text-sm`}
        //                 onClick={handleDeclineChanges}
        //                 disabled={isDisabled}
        //               >
        //                 Отменить
        //               </Button>
        //             </div>
        //           )}
        //         </div>
        //       </div>
        //     </div>
        //   );
        // }

        if (showChatIntegrations && msg.type === 'calendar_preview') {
          return <CalendarMessage key={msg.id} msg={msg} onReply={onReply} />;
        }

        if (showChatIntegrations && msg.type === 'jira_preview') {
          const isLast = messages[messages.length - 1]?.id === msg.id;
          return (
            <JiraPreviewMessage
              key={msg.id}
              msg={msg}
              ws={ws}
              setMessages={setMessages}
              token={localStorage.getItem('token') ?? ''}
              onReply={onReply}
              // isMock
              isLast={isLast}
            />
          );
        }

        if (showChatIntegrations && msg.type === 'jira_action_result') {
          return <JiraActionResultMessage key={msg.id} msg={msg} />;
        }

        if (msg.type === 'progress') {
          return (
            <div key={msg.id} className="flex justify-end mb-3 text-gray-600 text-md">
              <MessageWithDelay
                text={`${msg.payload?.text} (${msg.payload?.step}/${msg.payload?.total})`}
                delay={10}
              />
            </div>
          );
        }

        if (msg.type === 'status') {
          return <StatusMessage key={msg.id} msg={msg} theme={theme} />;
        }

        return null;
      })}

      {thinking && (
        <div className="flex justify-end mb-4 text-gray-400">
          <MessageWithDelay text={'думаю...'} delay={10} />
        </div>
      )}

      {isProcessing && (
        <div
          className={`flex justify-end mb-4 ${theme === 'light' ? 'text-gray-500' : 'text-gray-200'} text-md items-center gap-2`}
        >
          <MessageWithDelay text={'думаю...'} delay={10} />
          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      )}

      <div ref={endRef} />
    </Box>
  );
};

export default memo(MessagesList);
